import { useState, useEffect, useMemo } from 'react';
import type { Device, Group } from '../types';
import { api } from '../utils/api';
import { getUserCompany } from '../utils/user';
import { useAuth } from '../hooks/useAuth';
import { numericLocaleCompare } from '../utils/sortUtils';
import DeviceModal from './DeviceModal';
import DeviceSwitchHistoryModal from './DeviceSwitchHistoryModal';

interface DeviceTableProps {
  devices: Device[];
  id?: string;
}

interface DeviceIMEI {
  id: string;
  device_id: string;
  imei: string;
  data_source_name: string;
  package_name?: string;
  created_at: string;
  updated_at: string;
}

export default function DeviceTable({ devices: initialDevices, id }: DeviceTableProps) {
  const { user } = useAuth();
  const [devices, setDevices] = useState(initialDevices);
  const [groups, setGroups] = useState<Group[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<Device | undefined>();
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [selectedDeviceForHistory, setSelectedDeviceForHistory] = useState<Device | undefined>();
  const [deviceIMEIMap, setDeviceIMEIMap] = useState<Record<string, DeviceIMEI[]>>({});

  // Check if user can edit devices
  const canEdit = user?.role === 'administrator' || user?.role === 'owner';

  // Fetch groups on mount
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const fetchedGroups = await api.getAllGroups();
        setGroups(fetchedGroups);
      } catch (error) {
        console.warn('Failed to fetch groups:', error);
      }
    };
    fetchGroups();
  }, []);

  // Fetch IMEI data for all devices
  useEffect(() => {
    const fetchIMEIData = async () => {
      try {
        if (devices.length === 0) return;
        const deviceIds = devices.map(d => d.id);
        const imeiData = await api.getDeviceIMEIBatch(deviceIds);
        setDeviceIMEIMap(imeiData);
      } catch (error) {
        console.warn('Failed to fetch IMEI data:', error);
      }
    };
    if (devices.length > 0) {
      fetchIMEIData();
    }
  }, [devices]);

  // Helper function to get group name by ID
  const getGroupName = (groupId: string | null | undefined): string => {
    if (!groupId) return '-';
    const group = groups.find(g => g.id === groupId);
    return group?.name || '-';
  };

  // Helper function to mask email - show only local@domain (without TLD)
  const maskEmail = (email: string | null | undefined): string => {
    if (!email) return '-';
    
    const [localPart, domain] = email.split('@');
    if (!domain) return email; // Invalid email format
    
    // Get domain without TLD (e.g., "kerjain" from "kerjain.id")
    // const domainParts = domain.split('.');
    // const mainDomain = domainParts[0];
    
    return `${localPart}`;
    // return `${localPart}@${mainDomain}`;
  };

  // Hydrate devices on client: fetch by companyOwner
  useEffect(() => {
    const hydrateDevices = async () => {
      try {
        const companyOwner = getUserCompany();
        if (!companyOwner) return;
        const fetchedDevices = await api.getDevicesByCompany(companyOwner);
        setDevices(fetchedDevices);
      } catch (error) {
        console.warn('Failed to fetch devices by companyOwner:', error);
      }
    };
    hydrateDevices();
  }, []);

  // Memoized filtered and sorted devices
  const filteredDevices = useMemo(() => {
    let result = [...devices];

    if (searchTerm) {
      const term = searchTerm.toLowerCase().trim();
      result = result.filter(
        (device) =>
          (device.name?.toLowerCase() || '').includes(term) || 
          (device.mac?.toLowerCase() || '').includes(term) ||
          (device.email_pic?.toLowerCase() || '').includes(term) ||
          (device.idEmployee?.toLowerCase() || '').includes(term) ||
          (device.employee?.NIP?.toLowerCase() || '').includes(term) ||
          (device.employee?.Nama?.toLowerCase() || '').includes(term) ||
          getGroupName(device.group_id).toLowerCase().includes(term),
      );
    }

    if (statusFilter) {
      result = result.filter((device) => device.status === statusFilter);
    }

    // Sort devices by Device Name with numeric awareness (1, 2, 3... not 1, 10, 11, 2)
    result.sort((a, b) => {
      const nameA = (a.name || 'Unnamed Device').toLowerCase();
      const nameB = (b.name || 'Unnamed Device').toLowerCase();
      return numericLocaleCompare(nameA, nameB);
    });

    return result;
  }, [devices, searchTerm, statusFilter, groups]);

  // Handle search with debounce
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
  };

  // Handle status filter
  const handleStatusFilter = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(event.target.value);
  };

  // Handle add device
  // const handleAddDevice = () => {
  //   setSelectedDevice(undefined);
  //   setIsModalOpen(true);
  // };

  // Handle edit device
  const handleEditDevice = (device: Device) => {
    setSelectedDevice(device);
    setIsModalOpen(true);
  };

  // Handle view history
  const handleViewHistory = (device: Device) => {
    setSelectedDeviceForHistory(device);
    setIsHistoryModalOpen(true);
  };

  // Handle modal success
  const handleModalSuccess = async () => {
    try {
      const companyOwner = getUserCompany();
      if (!companyOwner) return;
      const fetchedDevices = await api.getDevicesByCompany(companyOwner);
      setDevices(fetchedDevices);
    } catch (error) {
      console.warn('Failed to refresh devices:', error);
    }
  };

  useEffect(() => {
    const handleUpdate = (event: CustomEvent<Device[]>) => {
      setDevices(event.detail);
    };

    const eventListener = handleUpdate as EventListener;
    const tableElement = document.getElementById(id || '');

    if (tableElement) {
      tableElement.addEventListener('update-devices', eventListener);

      return () => {
        tableElement.removeEventListener('update-devices', eventListener);
      };
    }
  }, [id]);

  return (
    <div className='space-y-4'>
      {/* Search and Filter Controls */}
      <div className='p-4 border border-gray-200'>
        <div className='flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4'>
          <div className='flex flex-col sm:flex-row gap-4 w-full sm:w-auto'>
            <div className='relative w-full sm:w-64'>
              <input
                type='text'
                value={searchTerm}
                onChange={handleSearch}
                placeholder='Search devices...'
                className='w-full pl-10 pr-4 py-2 border border-gray-200 hover:bg-gray-50 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500'
              />
              <svg
                className='w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='2'
                  d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
                />
              </svg>
            </div>
            <select
              value={statusFilter}
              onChange={handleStatusFilter}
              className='w-full sm:w-auto border border-gray-200 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500'
            >
              <option value=''>All Status</option>
              <option value='published'>Active</option>
              <option value='draft'>Inactive</option>
              <option value='offline'>Offline</option>
            </select>
          </div>
          {/* {canEdit && (
            <button 
              onClick={handleAddDevice}
              className='w-full sm:w-auto bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors'
            >
              + Add Device
            </button>
          )} */}
        </div>
      </div>

      {/* Table Container with Horizontal Scroll */}
      <div className='overflow-x-auto'>
        <div className='inline-block min-w-full align-middle'>
          <div className='overflow-hidden'>
            <table className='min-w-full divide-y divide-gray-200'>
              <thead className='bg-gray-50'>
                <tr>
                  <th
                    scope='col'
                    className='px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'
                  >
                    Device Name
                  </th>
                  <th
                    scope='col'
                    className='px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'
                  >
                    IMEI
                  </th>
                  <th
                    scope='col'
                    className='hidden sm:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'
                  >
                    ID Employee & Name
                  </th>
                  <th
                    scope='col'
                    className='hidden lg:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'
                  >
                    Group
                  </th>
                  <th
                    scope='col'
                    className='px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'
                  >
                    Status
                  </th>
                  <th
                    scope='col'
                    className='hidden md:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'
                  >
                    Access
                  </th>
                  <th
                    scope='col'
                    className='px-3 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className='bg-white divide-y divide-gray-200'>
                {filteredDevices.map((device) => (
                  <tr key={device.id} className='hover:bg-gray-50'>
                    <td className='px-3 sm:px-6 py-4 whitespace-nowrap'>
                      <div className='flex items-center'>
                        <div className='min-w-0 flex-1'>
                          <div className='text-sm font-medium text-gray-900 truncate max-w-[200px] sm:max-w-xs'>
                            {device.name || 'Unnamed Device'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className='px-3 sm:px-6 py-4 whitespace-nowrap'>
                      <div className='flex items-center'>
                        <div className='min-w-0 flex-1'>
                          {(() => {
                            const imeiList = deviceIMEIMap[device.id] || [];
                            if (imeiList.length === 0) {
                              return <div className='text-sm text-gray-500'>-</div>;
                            }
                            if (imeiList.length === 1) {
                              return (
                                <div className='text-sm font-medium text-gray-900 truncate max-w-[200px] sm:max-w-xs'>
                                  {imeiList[0].imei}
                                </div>
                              );
                            }
                            // Multiple IMEIs - show conflict badge
                            return (
                              <div className='relative group'>
                                <span className='inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full cursor-help'>
                                  {/* <span>⚠️</span> */}
                                  <span>Multiple IMEI Detected</span>
                                </span>
                                {/* Tooltip */}
                                <div className='absolute bottom-full left-0 mb-2 hidden group-hover:block bg-gray-900 text-white text-xs rounded py-2 px-3 whitespace-nowrap z-10'>
                                  <div className='font-semibold mb-1'>IMEIs registered:</div>
                                  {imeiList.map((imei, idx) => (
                                    <div key={idx} className='text-xs'>
                                      {idx + 1}. {imei.imei}
                                    </div>
                                  ))}
                                  <div className='absolute top-full left-2 -mt-1 border-4 border-transparent border-t-gray-900'></div>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </td>
                    <td className='hidden sm:table-cell px-6 py-4 whitespace-nowrap'>
                      <div className='flex items-center'>
                        {device.employee?.Foto ? (
                          <img
                            src={device.employee.Foto}
                            alt={device.employee.Nama}
                            className='w-8 sm:w-10 h-8 sm:h-10 rounded-full object-cover'
                          />
                        ) : (
                          <div className='w-8 sm:w-10 h-8 sm:h-10 bg-gray-200 rounded-full flex items-center justify-center'>
                            <span className='text-gray-600 font-medium'>
                              {device.employee?.Nama?.charAt(0) || device.name?.charAt(0) || 'D'}
                            </span>
                          </div>
                        )}
                        <div className='ps-2'>
                          {device.employee?.Nama && (
                            <div className='text-sm text-gray-900 truncate max-w-[150px]' title={device.employee.Nama}>
                              {device.employee.Nama}
                            </div>
                          )}
                          <div className='text-xs text-gray-500 truncate max-w-[150px]' title={device.employee?.NIP || device.idEmployee || 'Not Assigned'}>
                            {device.employee?.NIP || device.idEmployee || 'Not Assigned'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className='hidden lg:table-cell px-6 py-4 whitespace-nowrap'>
                      <div className='text-sm text-gray-900 truncate max-w-[150px]' title={getGroupName(device.group_id)}>
                        {getGroupName(device.group_id)}
                      </div>
                    </td>
                    <td className='px-3 sm:px-6 py-4 whitespace-nowrap'>
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${device.status === 'published' ? 'bg-green-100 text-green-800' : device.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :'bg-gray-100 text-gray-800'}`}>{device.status === 'published' ? 'Active' : device.status === 'draft' ? 'Inactive' : 'Offline'}</span>
                    </td>
                    <td className='hidden md:table-cell px-6 py-4 whitespace-nowrap'>
                      <div className='text-sm text-gray-500 truncate max-w-[200px]' title={device.email_pic || ''}>
                        {maskEmail(device.email_pic)}
                      </div>
                    </td>
                    <td className='px-3 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium'>
                      <div className='flex gap-2 justify-end'>
                        {canEdit && (
                          <button
                            onClick={() => handleEditDevice(device)}
                            className='text-blue-600 hover:text-blue-900'
                          >
                            Edit
                          </button>
                        )}
                        <button
                          onClick={() => handleViewHistory(device)}
                          className='text-purple-600 hover:text-purple-900'
                          title='View device switching history'
                        >
                          History
                        </button>
                        {/* <a href={`/devices/${device.id}`} className='text-gray-600 hover:text-gray-900'>
                          View
                        </a> */}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Pagination */}
      <div className='px-3 sm:px-6 py-4 border border-gray-200 hover:bg-gray-50'>
        <div className='flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4'>
          <div className='text-sm text-gray-500 w-full sm:w-auto'>
            Showing {filteredDevices.length} of {devices.length} results
          </div>
          <div className='flex space-x-2 w-full sm:w-auto justify-center sm:justify-end'>
            <button className='px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50' disabled>
              Previous
            </button>
            <button className='px-3 py-1 border rounded bg-blue-50 text-blue-600'>1</button>
            <button className='px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50' disabled>
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Modal */}
      <DeviceModal
        isOpen={isModalOpen}
        device={selectedDevice}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleModalSuccess}
      />

      {/* Device Switch History Modal */}
      <DeviceSwitchHistoryModal
        isOpen={isHistoryModalOpen}
        deviceId={selectedDeviceForHistory?.id}
        onClose={() => setIsHistoryModalOpen(false)}
      />
    </div>
  );
}
